import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  format,
  parseISO,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  isValid,
  addDays,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
} from "@/components/ui/full-calendar/calendar";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle } from "lucide-react";
import {
  FacilitySchedule,
  FacilityScheduleSlot,
  ScheduleType,
} from "@/types/facility/schedule";
import { useGetAllFacilityScheduleSlotQuery } from "@/services/facilityApis";
import { Control, FieldErrors, useFormContext, UseFormWatch } from "react-hook-form";

// Extend CalendarEvent type for our use case
interface FacilitySlotEvent extends CalendarEvent {
  is_exclusive?: boolean;
  is_consumed?: boolean;
  originalData?: FacilityScheduleSlot;
}

interface SlotAndScheduleProps {
  control?: Control<FacilitySchedule>;
  errors?: FieldErrors<FacilitySchedule>;
  watch?: UseFormWatch<FacilitySchedule>;
  open: boolean;
}

const SlotsandSchedule = ({ control, open }: SlotAndScheduleProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const form = useFormContext<FacilitySchedule>();
  const { watch, setError, clearErrors } = form;
  const watcher = watch();

  // Watch form data reactively from previous steps
  const facilityId = watch("general_info.facility_id");
  const startDate = watch("general_info.start_date") as string;
  const endDate = watch("general_info.end_date") as string;
  const alwaysAvailable = watch("general_info.always_available");
  const durationPerSlot = watch("general_info.duration_per_slot");
  const breakDuration = watch("general_info.break_duration") as number | undefined;
  const availability = watch("general_info.availability") as any;

  // Get current schedule ID for filtering (in edit mode)
  const currentScheduleId = watch("general_info.id");

  // State for current month view
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (startDate && startDate !== "") {
      try {
        const date = new Date(startDate);
        // Ensure we're using a valid date
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch (error) {
        console.warn("Invalid start date:", startDate, error);
      }
    }
    return new Date();
  });

  const [selectedSlot, setSelectedSlot] = useState<FacilitySlotEvent | null>(
    null
  );
  const [isViewSlotOpen, setIsViewSlotOpen] = useState(false);

  // Generate query string for API call with date range
  const generateQueryString = useCallback(
    (dateRange: { start: Date; end: Date }) => {
      const params = new URLSearchParams();
      params.append("facility_id", facilityId?.toString() || "");
      params.append("start_date", format(dateRange.start, "yyyy-MM-dd"));
      params.append("end_date", format(dateRange.end, "yyyy-MM-dd"));
      return params.toString();
    },
    [facilityId]
  );

  // Get date range for API call - ALWAYS use current visible month
  const apiDateRange = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    return { start: monthStart, end: monthEnd };
  }, [currentDate]);

  // Fetch existing facility schedule slots with date range
  const { data: existingSlots, isLoading: isLoadingSlots } =
    useGetAllFacilityScheduleSlotQuery(
      {
        query: generateQueryString(apiDateRange) + "&load_consumed_slots=true",
        orgId: orgId,
      },
      {
        skip: !facilityId || !orgId,
      }
    );

  // Update current date when start date changes
  useEffect(() => {
    if (startDate && startDate !== "") {
      try {
        const date = new Date(startDate);
        if (!isNaN(date.getTime())) {
          setCurrentDate(date);
        }
      } catch (error) {
        console.warn("Invalid start date in useEffect:", startDate, error);
      }
    } else {
      // If no start date, use current date
      setCurrentDate(new Date());
    }
  }, [startDate]);

  // Transform API slots to calendar events
  const transformSlotToEvent = (
    slot: FacilityScheduleSlot,
    isCurrentSchedule: boolean = false
  ): FacilitySlotEvent | null => {
    try {
      // Don't skip current schedule slots in edit mode - we want to show them
      // Only skip other schedules' slots if needed

      // Parse the slot date and times
      const slotDate = slot.slot_date;
      const startDateTime = `${slotDate}T${slot.start_time}`;
      const endDateTime = `${slotDate}T${slot.end_time}`;

      const start = parseISO(startDateTime);
      const end = parseISO(endDateTime);

      // Validate the parsed dates
      if (!isValid(start) || !isValid(end)) {
        console.warn("Invalid date found in slot:", slot);
        return null;
      }

      // Check if slot is consumed (booked)
      const isConsumed = slot.consumed_slots && slot.consumed_slots.length > 0;
      const consumedSlot = isConsumed ? slot.consumed_slots?.[0] : null;

      // Determine color based on slot status and whether it's current schedule
      let color = "green"; // Default: available
      let label = "Available";

      if (isConsumed) {
        color = "red";
        label = "Booked";
      } else if (isCurrentSchedule) {
        color = "orange"; // Current schedule slots
        label = "Current Schedule";
      }

      // Determine slot type tag based on schedule type
      const slotTypeTag = slot.type === ScheduleType.Events 
        ? { label: "Event", color: "blue" }
        : { label: "Booking", color: "purple" };

      return {
        id: slot.id.toString(),
        title: `${format(start, "HH:mm")} - ${format(end, "HH:mm")}`,
        start: start,
        end: end,
        color: color,
        tags: [
          slotTypeTag,
          {
            label: label,
            color: color,
          },
        ],
        user: {
          name: isConsumed
            ? consumedSlot?.member_name || "Booked"
            : isCurrentSchedule
              ? "Current Schedule"
              : "Available",
          avatar: isConsumed ? consumedSlot?.profile_img : undefined,
        },
        description: isConsumed
          ? `Booked by ${consumedSlot?.member_name} - Duration: ${slot.duration_hours}h`
          : isCurrentSchedule
            ? `Current schedule slot - Duration: ${slot.duration_hours}h`
            : `Available slot - Duration: ${slot.duration_hours}h`,
        location: slot.location || "Facility",
        is_exclusive: slot.is_exclusive,
        is_consumed: isConsumed,
        originalData: slot,
      } as FacilitySlotEvent;
    } catch (error) {
      console.warn("Error transforming slot to event:", slot, error);
      return null;
    }
  };

  // Generate slots based on availability and duration from form data
  const generateSlotsFromForm = useMemo(() => {
    if (!durationPerSlot || !availability || !facilityId) {
      return [];
    }

    const slots: FacilitySlotEvent[] = [];

    // Determine the date range for slot generation
    // We only generate slots for the month being viewed
    // but within the bounds of the overall schedule dates
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);

    let rangeStart: Date;
    let rangeEnd: Date;

    if (alwaysAvailable) {
      rangeStart = monthStart;
      rangeEnd = monthEnd;
    } else {
      const scheduleStart = startDate ? new Date(startDate) : monthStart;
      const scheduleEnd = endDate ? new Date(endDate) : monthEnd;

      // Start is the LATER of (beginning of month) and (schedule start)
      rangeStart = scheduleStart > monthStart ? scheduleStart : monthStart;
      // End is the EARLIER of (end of month) and (schedule end)
      rangeEnd = scheduleEnd < monthEnd ? scheduleEnd : monthEnd;
    }

    // If range is inverted, no slots to generate for this month
    if (rangeStart > rangeEnd) {
      return [];
    }


    // Generate slots for each day in the range
    let currentDay = rangeStart;
    while (currentDay <= rangeEnd) {
      const dayName = format(currentDay, "EEEE").toLowerCase(); // "monday", "tuesday", etc.
      const dayConfig = availability[dayName];

      // Since we're already iterating within the correct date range, just check day availability
      const dayDate = format(currentDay, "yyyy-MM-dd");

      if (
        dayConfig?.is_available &&
        dayConfig.time_slots.start_time &&
        dayConfig.time_slots.end_time
      ) {
        const startTime = dayConfig.time_slots.start_time;
        const endTime = dayConfig.time_slots.end_time;

        // Parse times
        const [startHours, startMinutes] = startTime.split(":").map(Number);
        const [endHours, endMinutes] = endTime.split(":").map(Number);

        let currentMinutes = startHours * 60 + startMinutes;
        let totalEndMinutes = endHours * 60 + endMinutes;

        // Check if this is an overnight range (end <= start means next day)
        const isOvernight = totalEndMinutes <= currentMinutes;

        // If overnight, adjust end time to be next day (add 24 hours)
        if (isOvernight) {
          totalEndMinutes += 24 * 60; // Add 24 hours for next day
        }

        const slotDurationMinutes = durationPerSlot * 60;
        const breakDurationMinutes = breakDuration || 0; // Break duration in minutes (default 0)

        let slotIndex = 0;
        while (currentMinutes + slotDurationMinutes <= totalEndMinutes) {
          // Calculate slot start time (modulo 24 hours to handle overnight)
          const slotStartMinutesOfDay = currentMinutes % (24 * 60);
          const slotStartHours = Math.floor(slotStartMinutesOfDay / 60);
          const slotStartMinutes = slotStartMinutesOfDay % 60;

          // Determine if slot start is on next day
          // For overnight ranges: if currentMinutes >= 1440, we've crossed to next day
          // For same-day ranges: slot start is always on currentDay
          let slotStartDay = currentDay;
          if (currentMinutes >= 24 * 60) {
            slotStartDay = addDays(currentDay, 1);
          }

          const slotStart = setMinutes(
            setHours(startOfDay(slotStartDay), slotStartHours),
            slotStartMinutes
          );

          // Calculate slot end time (modulo 24 hours to handle overnight)
          const slotEndTotalMinutes = currentMinutes + slotDurationMinutes;
          const slotEndMinutesOfDay = slotEndTotalMinutes % (24 * 60);
          const slotEndHours = Math.floor(slotEndMinutesOfDay / 60);
          const slotEndMinutesRemainder = slotEndMinutesOfDay % 60;

          // Determine if slot end is on next day
          // If end time (in minutes of day) is less than start time (in minutes of day), 
          // or if we've crossed the 24-hour mark, end is on next day
          let slotEndDay = slotStartDay;
          if (slotEndMinutesOfDay < slotStartMinutesOfDay || slotEndTotalMinutes >= 24 * 60) {
            slotEndDay = addDays(slotStartDay, 1);
          }

          const slotEnd = setMinutes(
            setHours(startOfDay(slotEndDay), slotEndHours),
            slotEndMinutesRemainder
          );


          slots.push({
            id: `${currentDay.getTime()}-${slotIndex}`,
            title: `${format(slotStart, "HH:mm")} - ${format(
              slotEnd,
              "HH:mm"
            )}`,
            start: slotStart,
            end: slotEnd,
            color: "blue", // Use blue for form-generated slots
            tags: [
              {
                label: "Booking",
                color: "purple",
              },
              {
                label: "Draft Slot",
                color: "blue",
              },
            ],
            user: {
              name: "Form Generated",
            },
            description: `Generated from form data - Duration: ${durationPerSlot}h`,
            location: watch("general_info.location") || "Facility",
            is_exclusive: watcher.general_info.is_exclusive,
          } as FacilitySlotEvent);

          // Move to next slot start time: current slot end + break duration
          // Break is only applied between slots (not after the last one)
          currentMinutes += slotDurationMinutes + breakDurationMinutes;
          slotIndex++;
        }
      }

      currentDay = addDays(currentDay, 1);
    }

    return slots;
  }, [
    currentDate,
    availability,
    durationPerSlot,
    breakDuration,
    startDate,
    endDate,
    alwaysAvailable,
    facilityId,
    watch("general_info.location"),
  ]);

  // Helper function to check if two time slots overlap
  const doSlotsOverlap = (slot1Start: Date, slot1End: Date, slot2Start: Date, slot2End: Date): boolean => {
    return slot1Start < slot2End && slot2Start < slot1End;
  };

  // Helper function to check if two slots are exactly the same
  const areSlotsExactMatch = (slot1Start: Date, slot1End: Date, slot2Start: Date, slot2End: Date): boolean => {
    return slot1Start.getTime() === slot2Start.getTime() && slot1End.getTime() === slot2End.getTime();
  };

  // Helper function to detect overlapping draft slots with existing available slots AND between draft slots
  const detectOverlappingSlots = useMemo(() => {
    const overlappingSlots: Array<{
      draftSlot: FacilitySlotEvent;
      existingSlot?: FacilityScheduleSlot;
      otherDraftSlot?: FacilitySlotEvent;
      overlapType: 'exact' | 'partial';
      exclusiveSlot: 'draft' | 'existing' | 'both';
    }> = [];

    // Get is_exclusive from form watcher (for draft slots)
    const draftIsExclusive = watcher.general_info.is_exclusive;

    // If no draft slots, return early
    if (generateSlotsFromForm.length === 0) {
      return { hasOverlaps: false, overlappingSlots: [] };
    }

    // Check for overlaps between draft slots themselves when is_exclusive is true
    if (draftIsExclusive && generateSlotsFromForm.length > 1) {
      for (let i = 0; i < generateSlotsFromForm.length; i++) {
        const draftSlot1 = generateSlotsFromForm[i];
        const draftStart1 = draftSlot1.start instanceof Date ? draftSlot1.start : new Date(draftSlot1.start);
        const draftEnd1 = draftSlot1.end instanceof Date ? draftSlot1.end : new Date(draftSlot1.end);

        for (let j = i + 1; j < generateSlotsFromForm.length; j++) {
          const draftSlot2 = generateSlotsFromForm[j];
          const draftStart2 = draftSlot2.start instanceof Date ? draftSlot2.start : new Date(draftSlot2.start);
          const draftEnd2 = draftSlot2.end instanceof Date ? draftSlot2.end : new Date(draftSlot2.end);

          // Check for exact match
          if (areSlotsExactMatch(draftStart1, draftEnd1, draftStart2, draftEnd2)) {
            overlappingSlots.push({
              draftSlot: draftSlot1,
              otherDraftSlot: draftSlot2,
              overlapType: 'exact',
              exclusiveSlot: 'draft'
            });
          }
          // Check for partial overlap
          else if (doSlotsOverlap(draftStart1, draftEnd1, draftStart2, draftEnd2)) {
            overlappingSlots.push({
              draftSlot: draftSlot1,
              otherDraftSlot: draftSlot2,
              overlapType: 'partial',
              exclusiveSlot: 'draft'
            });
          }
        }
      }
    }

    // Check for overlaps between draft slots and existing API slots
    if (existingSlots?.data && generateSlotsFromForm.length > 0) {
      // Get only available existing slots (not booked or current schedule slots)
      const availableExistingSlots = existingSlots.data.filter(slot =>
        (!slot.consumed_slots || slot.consumed_slots.length === 0) &&
        (!currentScheduleId || slot.facility_schedule_id !== currentScheduleId)
      );

      generateSlotsFromForm.forEach((draftSlot) => {
        const draftStart = draftSlot.start instanceof Date ? draftSlot.start : new Date(draftSlot.start);
        const draftEnd = draftSlot.end instanceof Date ? draftSlot.end : new Date(draftSlot.end);

        availableExistingSlots.forEach((existingSlot) => {
          const existingDate = existingSlot.slot_date;
          const existingStartDateTime = `${existingDate}T${existingSlot.start_time}`;
          const existingEndDateTime = `${existingDate}T${existingSlot.end_time}`;

          const existingStart = parseISO(existingStartDateTime);
          const existingEnd = parseISO(existingEndDateTime);

          if (!isValid(existingStart) || !isValid(existingEnd)) return;

          const existingIsExclusive = existingSlot.is_exclusive || false;
          
          // Only block duplicates/overlaps if at least one slot is exclusive
          // If both are non-exclusive, allow duplicates/overlaps (don't add to overlappingSlots)
          const shouldBlockOverlap = existingIsExclusive || draftIsExclusive;
          
          if (shouldBlockOverlap) {
            // Check for exact match - Blocked if either slot is exclusive
            if (areSlotsExactMatch(draftStart, draftEnd, existingStart, existingEnd)) {
              // Determine which slot(s) are exclusive
              let exclusiveSlot: 'draft' | 'existing' | 'both' = 'draft';
              if (existingIsExclusive && draftIsExclusive) {
                exclusiveSlot = 'both';
              } else if (existingIsExclusive) {
                exclusiveSlot = 'existing';
              }

              overlappingSlots.push({
                draftSlot,
                existingSlot,
                overlapType: 'exact',
                exclusiveSlot
              });
            }
            // Check for partial overlap - Blocked if either slot is exclusive
            else if (doSlotsOverlap(draftStart, draftEnd, existingStart, existingEnd)) {
              // Determine which slot(s) are exclusive
              let exclusiveSlot: 'draft' | 'existing' | 'both' = 'draft';
              if (existingIsExclusive && draftIsExclusive) {
                exclusiveSlot = 'both';
              } else if (existingIsExclusive) {
                exclusiveSlot = 'existing';
              }

              overlappingSlots.push({
                draftSlot,
                existingSlot,
                overlapType: 'partial',
                exclusiveSlot
              });
            }
          }
          // If both are non-exclusive, allow all overlaps (don't add to overlappingSlots)
        });
      });
    }

    return {
      hasOverlaps: overlappingSlots.length > 0,
      overlappingSlots
    };
  }, [existingSlots?.data, generateSlotsFromForm, currentScheduleId, watcher.general_info.is_exclusive]);

  // Combine API slots and form-generated slots
  const allEvents = useMemo(() => {
    // Create a map of overlapping draft slots for quick lookup (include both draft slots in draft-to-draft overlaps)
    const overlappingDraftSlotIds = new Set<string>();
    detectOverlappingSlots.overlappingSlots.forEach(overlap => {
      overlappingDraftSlotIds.add(overlap.draftSlot.id);
      if (overlap.otherDraftSlot) {
        overlappingDraftSlotIds.add(overlap.otherDraftSlot.id);
      }
    });

    if (!existingSlots?.data) {
      // No existing slots, but still need to mark overlapping draft slots
      return generateSlotsFromForm.map((draftSlot) => {
        const isOverlapping = overlappingDraftSlotIds.has(draftSlot.id);
        
        if (isOverlapping) {
          // Find the overlap details for this slot (could be as draftSlot or otherDraftSlot)
          const overlapDetails = detectOverlappingSlots.overlappingSlots.find(
            overlap => overlap.draftSlot.id === draftSlot.id || overlap.otherDraftSlot?.id === draftSlot.id
          );
          
          const isExactDuplicate = overlapDetails?.overlapType === 'exact';
          const overlapLabel = isExactDuplicate ? 'Duplicate' : 'Overlap';
          
          let overlapMessage = '';
          if (overlapDetails?.otherDraftSlot) {
            // Draft-to-draft overlap
            overlapMessage = isExactDuplicate 
              ? 'This slot exactly matches another draft slot'
              : 'This slot overlaps with another draft slot';
          } else {
            overlapMessage = isExactDuplicate 
              ? 'This slot has a duplicate'
              : 'This slot has an overlap';
          }
          
          return {
            ...draftSlot,
            color: "yellow",
            tags: [
              {
                label: "Booking",
                color: "purple",
              },
              {
                label: overlapLabel,
                color: "yellow",
              },
            ],
            description: `⚠️ ${overlapLabel.toUpperCase()}: ${overlapMessage} - Duration: ${durationPerSlot}h`,
          };
        }
        
        return draftSlot;
      });
    }

    // Separate current schedule slots from other slots
    const currentScheduleSlots: FacilitySlotEvent[] = [];
    const otherScheduleSlots: FacilitySlotEvent[] = [];

    existingSlots.data.forEach((slot) => {
      const isCurrentSchedule =
        currentScheduleId && slot.facility_schedule_id === currentScheduleId;
      const transformedSlot = transformSlotToEvent(
        slot,
        Boolean(isCurrentSchedule)
      );

      if (transformedSlot) {
        if (isCurrentSchedule) {
          currentScheduleSlots.push(transformedSlot);
        } else {
          otherScheduleSlots.push(transformedSlot);
        }
      }
    });

    // In edit mode, prioritize existing slots over draft slots
    if (currentScheduleId) {
      // Start with current schedule slots and other schedule slots
      const combined: FacilitySlotEvent[] = [...currentScheduleSlots, ...otherScheduleSlots];

      // Add all draft slots, but mark overlapping ones with warning colors
      generateSlotsFromForm.forEach((draftSlot) => {
        const draftStart = draftSlot.start instanceof Date ? draftSlot.start : new Date(draftSlot.start);
        const draftEnd = draftSlot.end instanceof Date ? draftSlot.end : new Date(draftSlot.end);

        // In edit mode, skip draft slots that exactly match current schedule slots
        // This prevents showing duplicate slots when the user hasn't changed the availability
        const matchesCurrentSchedule = currentScheduleSlots.some((currentSlot) => {
          const currentStart = currentSlot.start instanceof Date ? currentSlot.start : new Date(currentSlot.start);
          const currentEnd = currentSlot.end instanceof Date ? currentSlot.end : new Date(currentSlot.end);
          return areSlotsExactMatch(draftStart, draftEnd, currentStart, currentEnd);
        });

        if (matchesCurrentSchedule) {
          // Skip this draft slot - it already exists as a current schedule slot
          return;
        }

        const isOverlapping = overlappingDraftSlotIds.has(draftSlot.id);

        if (isOverlapping) {
          // Find the overlap details for this slot (could be as draftSlot or otherDraftSlot)
          const overlapDetails = detectOverlappingSlots.overlappingSlots.find(
            overlap => overlap.draftSlot.id === draftSlot.id || overlap.otherDraftSlot?.id === draftSlot.id
          );
          
          const isExactDuplicate = overlapDetails?.overlapType === 'exact';
          const overlapLabel = isExactDuplicate ? 'Duplicate' : 'Overlap';
          
          let overlapMessage = '';
          if (overlapDetails?.otherDraftSlot) {
            // Draft-to-draft overlap
            overlapMessage = isExactDuplicate 
              ? 'This slot exactly matches another draft slot'
              : 'This slot overlaps with another draft slot';
          } else if (overlapDetails?.existingSlot) {
            // Draft-to-existing overlap
            overlapMessage = isExactDuplicate 
              ? 'This slot exactly matches an existing available slot'
              : 'This slot overlaps with an existing available slot';
          } else {
            overlapMessage = isExactDuplicate 
              ? 'This slot has a duplicate'
              : 'This slot has an overlap';
          }
          
          // Mark as warning/duplicate slot
          const warningSlot = {
            ...draftSlot,
            color: "yellow",
            tags: [
              {
                label: "Booking",
                color: "purple",
              },
              {
                label: overlapLabel,
                color: "yellow",
              },
            ],
            description: `⚠️ ${overlapLabel.toUpperCase()}: ${overlapMessage} - Duration: ${durationPerSlot}h`,
          };
          combined.push(warningSlot);
        } else {
          // If not overlapping (not in error list), always add the draft slot
          // This allows non-exclusive duplicates to be shown
          combined.push(draftSlot);
        }
      });

      return combined;
    } else {
      // Create mode: Prefer existing API slots; add draft slots with conflict detection
      const combined = [...otherScheduleSlots];

      // Add form-generated slots (draft) with conflict detection
      generateSlotsFromForm.forEach((draftSlot) => {
        const isOverlapping = overlappingDraftSlotIds.has(draftSlot.id);

        if (isOverlapping) {
          // Find the overlap details for this slot (could be as draftSlot or otherDraftSlot)
          const overlapDetails = detectOverlappingSlots.overlappingSlots.find(
            overlap => overlap.draftSlot.id === draftSlot.id || overlap.otherDraftSlot?.id === draftSlot.id
          );
          
          const isExactDuplicate = overlapDetails?.overlapType === 'exact';
          const overlapLabel = isExactDuplicate ? 'Duplicate' : 'Overlap';
          
          let overlapMessage = '';
          if (overlapDetails?.otherDraftSlot) {
            // Draft-to-draft overlap
            overlapMessage = isExactDuplicate 
              ? 'This slot exactly matches another draft slot'
              : 'This slot overlaps with another draft slot';
          } else if (overlapDetails?.existingSlot) {
            // Draft-to-existing overlap
            overlapMessage = isExactDuplicate 
              ? 'This slot exactly matches an existing available slot'
              : 'This slot overlaps with an existing available slot';
          } else {
            overlapMessage = isExactDuplicate 
              ? 'This slot has a duplicate'
              : 'This slot has an overlap';
          }
          
          // Mark as warning/duplicate slot
          const warningSlot = {
            ...draftSlot,
            color: "yellow",
            tags: [
              {
                label: "Booking",
                color: "purple",
              },
              {
                label: overlapLabel,
                color: "yellow",
              },
            ],
            description: `⚠️ ${overlapLabel.toUpperCase()}: ${overlapMessage} - Duration: ${durationPerSlot}h`,
          };
          combined.push(warningSlot);
        } else {
          // If not overlapping (not in error list), always add the draft slot
          // This allows non-exclusive duplicates to be shown
          combined.push(draftSlot);
        }
      });

      return combined;
    }
  }, [existingSlots?.data, generateSlotsFromForm, currentScheduleId, detectOverlappingSlots, durationPerSlot]);

  // Navigation functions
  const handlePrevious = useCallback(() => {
    setCurrentDate((prev) => subMonths(prev, 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentDate((prev) => addMonths(prev, 1));
  }, []);

  const handleViewSlot = (event: FacilitySlotEvent) => {
    setSelectedSlot(event);
    setIsViewSlotOpen(true);
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // Check if we should show the calendar
  const shouldShowCalendar = facilityId && durationPerSlot;

  // Set form errors when overlaps are detected
  useEffect(() => {
    if (detectOverlappingSlots.hasOverlaps) {
      const overlaps = detectOverlappingSlots.overlappingSlots;
      const exactMatches = overlaps.filter(overlap => overlap.overlapType === 'exact');
      const partialOverlaps = overlaps.filter(overlap => overlap.overlapType === 'partial');

      let errorMessage = "";
      if (exactMatches.length > 0 && partialOverlaps.length > 0) {
        errorMessage = `${exactMatches.length} duplicate(s) and ${partialOverlaps.length} overlap(s) detected.`;
      } else if (exactMatches.length > 0) {
        errorMessage = `${exactMatches.length} exact slot duplicate(s) detected.`;
      } else {
        errorMessage = `${partialOverlaps.length} slot overlap(s) detected.`;
      }

      errorMessage += " Please adjust your availability settings to resolve conflicts.";

      setError("slot_overlaps", {
        type: "manual",
        message: errorMessage,
      });
    } else {
      // Clear the error when no overlaps
      clearErrors("slot_overlaps");
    }
  }, [detectOverlappingSlots, setError, clearErrors]);

  // Get form errors
  const formErrors = form.formState.errors;
  const slotOverlapError = formErrors.slot_overlaps as any;

  // Expose slotOverlapError to parent form via custom event
  useEffect(() => {
    const event = new CustomEvent("overlap-status-changed", {
      detail: { hasOverlap: !!slotOverlapError },
    });
    document.dispatchEvent(event);
  }, [slotOverlapError]);

  console.log({ allEvents });

  return (
    <div className="space-y-4 h-full flex flex-col" data-component="slots-and-schedule">
      <Card className="p-4 flex-1 flex flex-col">
        <div>
          <p className="font-medium text-base">Slots & Schedule Preview</p>
          <Separator className="h-[1px] mt-2 mb-4" />
        </div>

        {/* Form Error Display */}
        {/* {slotOverlapError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Form Validation Error
                </h3>
                <p className="mt-1 text-sm text-red-700">
                  {slotOverlapError.message}
                </p>
              </div>
            </div>
          </div>
        )} */}

        {!shouldShowCalendar && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-center text-amber-800">
              <Clock className="h-5 w-5 mr-2" />
              <span>
                Please complete the previous steps to view facility slots
              </span>
            </div>
          </div>
        )}

        {shouldShowCalendar ? (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Overlap Warning - Only shows for exact matches (duplicates) */}
            {detectOverlappingSlots.hasOverlaps && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Scheduling Conflicts Detected
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p className="mb-2">
                        {detectOverlappingSlots.overlappingSlots.length} slot conflict(s) detected:
                      </p>
                      <ul className="list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                        {detectOverlappingSlots.overlappingSlots
                          .map((overlap, index) => {
                            let exclusiveInfo = '';
                            if (overlap.exclusiveSlot === 'existing') {
                              exclusiveInfo = ' (existing slot is exclusive)';
                            } else if (overlap.exclusiveSlot === 'draft') {
                              if (overlap.otherDraftSlot) {
                                exclusiveInfo = ' (both slots are exclusive)';
                              } else {
                                exclusiveInfo = ' (your slot is exclusive)';
                              }
                            } else if (overlap.exclusiveSlot === 'both') {
                              exclusiveInfo = ' (both slots are exclusive)';
                            }
                            
                            // Check if this is a draft-to-draft overlap
                            if (overlap.otherDraftSlot) {
                              return (
                                <li key={index} className="text-xs">
                                  <strong>{overlap.draftSlot.title}</strong> on{' '}
                                  {format(overlap.draftSlot.start, "EEEE, MMM dd")}
                                  {overlap.overlapType === 'exact' ? ' is identical to ' : ' overlaps with '}
                                  another draft slot{' '}
                                  <strong>{overlap.otherDraftSlot.title}</strong> on{' '}
                                  {format(overlap.otherDraftSlot.start, "EEEE, MMM dd")}
                                  {exclusiveInfo}
                                </li>
                              );
                            } else {
                              // Draft-to-existing overlap
                              return (
                                <li key={index} className="text-xs">
                                  <strong>{overlap.draftSlot.title}</strong> on{' '}
                                  {format(overlap.draftSlot.start, "EEEE, MMM dd")}
                                  {overlap.overlapType === 'exact' ? ' is identical to ' : ' overlaps with '}
                                  existing slot{' '}
                                  <strong>{overlap.existingSlot!.start_time} - {overlap.existingSlot!.end_time}</strong>
                                  {exclusiveInfo}
                                </li>
                              );
                            }
                          })}
                      </ul>
                      <p className="mt-2 font-medium">
                        Please adjust your availability settings to avoid overlapping slots.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center space-x-4 mb-4 p-3 bg-gray-50 rounded-md flex-wrap">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm">Available Slots</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm">Booked Slots</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-sm">Draft Slots (Preview)</span>
              </div>
              {detectOverlappingSlots.hasOverlaps && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-sm">Duplicate Slots</span>
                </div>
              )}
              {currentScheduleId && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span className="text-sm">Current Schedule</span>
                </div>
              )}
              <div className="text-sm text-gray-600">
                Duration: {durationPerSlot}h per slot
              </div>
            </div>

            {/* Calendar */}
            <div className="relative w-full flex-1 min-h-[600px]">
              <FullCalendar
                events={allEvents}
                initialView="month"
                initialDate={currentDate}
                key={`calendar-${currentDate.getTime()}`} // Force re-render when date changes
                isCustomView={true}
                onCreateEvent={undefined} // Disable create event
                onViewEvent={handleViewSlot}
                onFilterEvents={undefined} // Disable filter
                onPrevious={handlePrevious}
                onNext={handleNext}
                disableNavigation={false}
                hideEventActions={true}
              />
            </div>

            {/* Slot Details Dialog */}
            <Dialog open={isViewSlotOpen} onOpenChange={setIsViewSlotOpen}>
              <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Slot Details
                  </DialogTitle>
                </DialogHeader>
                {selectedSlot && (
                  <div className="space-y-4">
                    <div className="grid gap-2 ">
                      <div className="flex gap-2 items-center">
                        {selectedSlot.originalData && (
                          <div className="flex gap-2 items-center">
                            <span className="font-medium">Slot ID:</span>
                            <p className="text-sm text-gray-600">
                              {selectedSlot.originalData.id}
                            </p>
                          </div>
                        )}
                        {selectedSlot.is_exclusive && (
                          <div>
                            <span
                              className={cn(
                                "px-2 py-1 rounded text-xs font-medium bg-red-500 text-white"
                              )}
                            >
                              Exclusive
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium">Time:</span>
                        <span className="ml-2">
                          {formatDate(selectedSlot.start)} -{" "}
                          {formatDate(selectedSlot.end)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium">Date:</span>
                        <span className="ml-2">
                          {format(selectedSlot.start, "EEEE, MMMM dd, yyyy")}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <span className="font-medium">Type:</span>
                        <span
                          className={cn(
                            "ml-2 px-2 py-1 rounded text-xs font-medium",
                            selectedSlot.color === "green"
                              ? "bg-green-100 text-green-800"
                              : selectedSlot.color === "red"
                                ? "bg-red-100 text-red-800"
                                : selectedSlot.color === "orange"
                                  ? "bg-orange-100 text-orange-800"
                                  : selectedSlot.color === "yellow"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-blue-100 text-blue-800"
                          )}
                        >
                          {selectedSlot.color === "green"
                            ? "Available Slot"
                            : selectedSlot.color === "red"
                              ? "Booked Slot"
                              : selectedSlot.color === "orange"
                                ? "Current Schedule"
                                : selectedSlot.color === "yellow"
                                  ? "Duplicate Draft Slot"
                                  : "Draft Slot (Preview)"}
                        </span>
                      </div>

                      {selectedSlot.is_consumed &&
                        selectedSlot.originalData?.consumed_slots?.[0] && (
                          <>
                            <div className="flex items-center">
                              <span className="font-medium">Booked by:</span>
                              <div className="flex items-center gap-2 ml-2">
                                {selectedSlot.originalData.consumed_slots[0]
                                  .profile_img && (
                                    <Avatar className="h-6 w-6">
                                      <AvatarImage
                                        src={`${import.meta.env.VITE_API_URL
                                          }/uploads/${selectedSlot.originalData
                                            .consumed_slots[0].profile_img
                                          }`}
                                        alt={
                                          selectedSlot.originalData
                                            .consumed_slots[0].member_name
                                        }
                                      />
                                      <AvatarFallback>
                                        {selectedSlot.originalData.consumed_slots[0].member_name
                                          .charAt(0)
                                          .toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                  )}
                                <span>
                                  {
                                    selectedSlot.originalData.consumed_slots[0]
                                      .member_name
                                  }
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium">Email:</span>
                              <span className="ml-2">
                                {
                                  selectedSlot.originalData.consumed_slots[0]
                                    .member_email
                                }
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium">Booking ID:</span>
                              <span className="ml-2">
                                {
                                  selectedSlot.originalData.consumed_slots[0]
                                    .booking_id
                                }
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium">Booked on:</span>
                              <span className="ml-2">
                                {format(
                                  new Date(
                                    selectedSlot.originalData.consumed_slots[0].created_at
                                  ),
                                  "PPP p"
                                )}
                              </span>
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <DialogClose asChild>
                    <Button>Close</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        ) : <></>}
      </Card>
    </div>
  );
};

export default SlotsandSchedule;
